import { Injectable } from '@angular/core';

export const enum CacheStorageModeEnum {
  MEMORY,
  SESSION,
  LOCAL
}

export interface CacheEntry {
  value: any;
  expiry: number | null;
  tag?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private prefix = '';
  private storageMode: CacheStorageModeEnum = CacheStorageModeEnum.MEMORY;
  private memoryStore = new Map<string, CacheEntry>();

  setGlobalPrefix(prefix: string) {
    this.prefix = prefix + ':';
  }

  useStorage(mode: CacheStorageModeEnum): CacheService {
    this.storageMode = mode;
    return this;
  }

  private getStore() {
    switch (this.storageMode) {
      case CacheStorageModeEnum.SESSION:
        /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Window/sessionStorage) */
        return sessionStorage;
      case CacheStorageModeEnum.LOCAL:
        /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/Window/localStorage) */
        return localStorage;
      default:
        return this.memoryStore;
    }
  }

  private makeKey(key: string): string {
    return this.prefix + key;
  }

  set(key: string, value: any, options?: { tag?: string; maxAge?: number }): boolean {
    const expiry = options?.maxAge ? Date.now() + options.maxAge : null;
    const entry: CacheEntry = { value, expiry, tag: options?.tag };

    const store = this.getStore();
    const storageKey = this.makeKey(key);

    if (store instanceof Map) {
      // Cached in memory
      store.set(storageKey, entry);
    } else {
      // Cached in session/local storage
      store.setItem(storageKey, JSON.stringify(entry));
    }

    return true;
  }

  get<T>(key: string): T | null {
    const store = this.getStore();
    const storageKey = this.makeKey(key);
    let entry: CacheEntry | null = null;

    if (store instanceof Map) {
      // Cached in memory
      entry = store.get(storageKey) || null;
    } else {
      // Cached in session/local storage
      const raw = store.getItem(storageKey);
      entry = raw ? JSON.parse(raw) : null;
    }

    if (!entry) return null;

    if (entry.expiry && Date.now() > entry.expiry) {
      this.remove(key);
      return null;
    }

    return entry.value;
  }

  exists(key: string): boolean {
    return !!this.get(key);
  }

  remove(key: string): void {
    const store = this.getStore();
    const storageKey = this.makeKey(key);

    if (store instanceof Map) {
      // Cached in memory
      store.delete(storageKey);
    } else {
      // Cached in session/local storage
      store.removeItem(storageKey);
    }
  }

}